import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowDown, FiChevronRight, FiCrosshair } from 'react-icons/fi';
import { Container } from '../ui';
import WhatsAppIcon from '../icons/WhatsAppIcon';
import { buildImageUrl } from '../../services/api';
import { LocationPage } from '../../types/location';
import { formatCoordinates, kindLabel } from './locationModel';
import { actionBase, ink } from './tokens';

const Hero = styled.section`
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: flex-end;
  min-height: clamp(560px, 82vh, 780px);
  /* topo reservado para a trilha de navegação, que é absoluta */
  padding: 5rem 0 calc(4.5rem + 56px);
  color: #fff;
  background: ${({ theme }) => ink(theme)};
  overflow: hidden;

  @media (max-width: 640px) {
    min-height: 600px;
    padding-top: 4.5rem;
    padding-bottom: calc(2.5rem + 40px);
  }
`;

const Photo = styled.img`
  position: absolute;
  inset: 0;
  z-index: -2;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 55%;
  animation: settle 1.6s cubic-bezier(0.2, 0.7, 0.2, 1) both;

  @keyframes settle {
    from { transform: scale(1.06); opacity: 0.4; }
    to { transform: scale(1); opacity: 1; }
  }
`;

const Shade = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    linear-gradient(180deg, rgba(20, 12, 6, 0.55) 0%, rgba(20, 12, 6, 0.05) 26%,
      rgba(20, 12, 6, 0.2) 52%, rgba(20, 12, 6, 0.88) 100%),
    linear-gradient(90deg, rgba(20, 12, 6, 0.55) 0%, rgba(20, 12, 6, 0) 62%);
`;

const Crumbs = styled.nav`
  position: absolute;
  top: 1.4rem;
  left: 0;
  right: 0;

  ol {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.82rem;
    color: rgba(255, 255, 255, 0.78);
  }
  li { display: inline-flex; align-items: center; gap: 0.35rem; }
  a:hover { color: #fff; text-decoration: underline; }
  [aria-current] { color: #fff; }
`;

const Content = styled.div`
  max-width: 820px;
`;

const Kind = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem 0.9rem;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};

  &::before {
    content: '';
    width: 36px;
    height: 1px;
    background: currentColor;
  }

  /* No celular a cidade desce para a linha de baixo, sem fio nem separador. */
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.35rem;
    &::before, .separator { display: none; }
  }
`;

const Name = styled.h1`
  margin-top: 1.1rem;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 600;
  font-size: clamp(2.5rem, 6.4vw, 5rem);
  line-height: 1.02;
  letter-spacing: -0.035em;
  color: #fff;
  text-wrap: balance;
  text-shadow: 0 2px 30px rgba(0, 0, 0, 0.25);
`;

const Headline = styled.p`
  margin-top: 1.25rem;
  max-width: 56ch;
  font-size: clamp(1.05rem, 1.6vw, 1.25rem);
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.88);
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: 2.1rem;

  @media (max-width: 480px) {
    a { flex: 1 1 100%; }
  }
`;

const Primary = styled.a`
  ${actionBase}
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => ink(theme)};
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5);

  &:hover { background: #fff; }
`;

const Secondary = styled.a`
  ${actionBase}
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(6px);

  svg { color: #25d366; }
  &:hover { background: rgba(255, 255, 255, 0.18); border-color: #fff; }
`;

const Survey = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.72);

  @media (min-width: 900px) {
    position: absolute;
    right: 0;
    bottom: 0.35rem;
  }
  @media (max-width: 899px) {
    margin-top: 1.6rem;
  }
`;

const Inner = styled(Container)`
  position: relative;
`;

interface Props {
  page: LocationPage;
  whatsHref: string | null;
  onWhatsApp: () => void;
}

const LocationHero: React.FC<Props> = ({ page, whatsHref, onWhatsApp }) => {
  const hasPoint = page.latitude != null && page.longitude != null;
  return (
    <Hero aria-labelledby="local-nome">
      {page.hero_image && <Photo src={buildImageUrl(page.hero_image)} alt="" loading="eager" />}
      <Shade />
      <Crumbs aria-label="Navegação estrutural">
        <Container>
          <ol>
            <li><Link to="/">Início</Link><FiChevronRight size={13} aria-hidden /></li>
            <li><Link to="/imoveis">Imóveis</Link><FiChevronRight size={13} aria-hidden /></li>
            <li aria-current="page">{page.name}</li>
          </ol>
        </Container>
      </Crumbs>
      <Inner>
        <Content>
          <Kind>
            <span>{kindLabel(page.kind)}</span>
            {page.city && <span className="separator" aria-hidden>·</span>}
            {page.city && <span>{page.city}</span>}
          </Kind>
          <Name id="local-nome">{page.name}</Name>
          {page.headline && <Headline>{page.headline}</Headline>}
          <Actions>
            {page.stats.total > 0 && (
              <Primary href="#imoveis">
                Ver imóveis <FiArrowDown size={17} aria-hidden />
              </Primary>
            )}
            {whatsHref && (
              <Secondary href={whatsHref} target="_blank" rel="noopener noreferrer" onClick={onWhatsApp}>
                <WhatsAppIcon size={19} /> Falar no WhatsApp
              </Secondary>
            )}
          </Actions>
        </Content>
        {hasPoint && (
          <Survey>
            <FiCrosshair size={14} aria-hidden />
            <span>{formatCoordinates(page.latitude as number, page.longitude as number)}</span>
          </Survey>
        )}
      </Inner>
    </Hero>
  );
};

export default LocationHero;
